<template>
	<div class="flex flex-col justify-between mt-4">
		<div v-if="basket?.Items?.length" class="flex flex-col">
			<div
				class="mb-2 pb-[2px] hidden sm:flex text-sm font-bold text-gray-600 border-b border-gray-dark"
			>
				<p class="px-2.5 w-[40%] invisible">
					{{ $t("checkout.item") }}
				</p>
				<p class="px-2.5 w-[20%]">
					{{ $t("checkout.price") }}
				</p>
				<p class="px-2.5 w-[20%]">
					{{ $t("checkout.quantity") }}
				</p>
				<p class="px-2.5 w-[20%]">
					{{ $t("checkout.total") }}
				</p>
			</div>
			<ul class="mb-8">
				<CheckoutOrderItem
					v-for="item in itemsRef"
					:key="item.Id"
					:line-item="item"
				/>
			</ul>
			<div
				class="grid sm:grid-cols-2 grid-cols-1 justify-between items-start sm:gap-0 gap-4 px-6 pb-6"
			>
				<div class="grid gap-2 w-full">
					<SharedCheckbox
						id="agree"
						v-model="hasDiscountCode"
						:content="$t('Add discount code')"
					/>
					<div v-if="hasDiscountCode" class="flex items-center gap-2">
						<div class="relative max-w-[300px] w-full">
							<TrashIcon
								class="shrink-0 cursor-pointer text-gray-700 ml-auto h-6 w-6 absolute right-1.5 top-1.5 z-60"
								@click="handleDiscountCode('delete')"
							/>
							<input
								v-model="discountCode"
								name="discount code"
								type="text"
								class="appearance-none relative block w-full px-1.5 py-[6px] border-[2px] border-gray-400 placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:z-10 sm:text-sm bg-white"
								:placeholder="$t('checkout.discount.placeholder')"
							/>
						</div>
						<button
							type="button"
							class="py-1 px-2.5 text-white bg-blue-dark hover:bg-blue-darker border border-transparent"
							@click="handleDiscountCode('apply')"
						>
							{{ $t("checkout.discount.button") }}
						</button>
					</div>
				</div>
				<div class="flex flex-col gap-3 text-sm">
					<div class="flex flex-col">
						<div class="flex justify-between font-bold">
							<span>{{ $t("Subtotal") }}</span>
							<span>{{ formatPrice(basket.Summary?.Items.AmountIncVat) }}</span>
						</div>
						<div
							v-for="promotion in appliedPromotion"
							:key="promotion.Id"
							class="flex justify-between"
						>
							<span
								>{{ $t("Discount code") }} ({{ promotion.DiscountCode }})</span
							>
							<span>{{
								formatPrice(promotion?.AppliedAmountIncVat || 0)
							}}</span>
						</div>
						<div class="flex justify-between">
							<span>{{ $t("Shipping") }}</span>
							<span>{{
								formatPrice(basket.Summary?.Freigt.AmountIncVat)
							}}</span>
						</div>
					</div>
					<div class="flex flex-col">
						<div class="flex justify-between text-lg font-bold">
							<span>{{ $t("Order total") }}</span>
							<span>{{ formatPrice(basket.Summary?.Total.AmountIncVat) }}</span>
						</div>
						<div class="flex justify-between">
							<span>{{ $t("Whereof VAT") }}</span>
							<span>{{ formatPrice(basket.Summary?.Total.Vat) }}</span>
						</div>
						<div
							v-if="shippingItem"
							class="mt-5 flex items-center gap-2 text-sm text-gray-500"
						>
							<InformationCircleIcon class="h-6 w-6" />
							{{
								$t(
									"Total price includes the '{method}' default shipping method at {cost}.",
									{
										method: shippingItem.Name,
										cost: formatPrice(shippingItem.PriceDisplayIncVat)
									}
								)
							}}
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-else class="flex flex-col gap-3">
			<span class="text-sm text-gray-700">
				{{ $t("checkout.empty_basket") }}
			</span>
			<nuxt-link
				:to="localePath('/')"
				class="py-3 px-5 w-full text-center text-xl text-white no-underline bg-blue-dark hover:bg-blue-darker"
			>
				{{ $t("checkout.shopping") }}
			</nuxt-link>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, toRef, watch } from "vue";
import { TrashIcon, InformationCircleIcon } from "@heroicons/vue/24/outline";
import type {
	Basket,
	BasketItem
} from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import CheckoutOrderItem from "../components/CheckoutOrderItem.vue";
import { isShippingItem } from "~/utils/cart/isShippingItem";

interface Props {
	basket: Basket;
	orderId: string;
}

const props = defineProps<Props>();

const localePath = useLocalePath();
const cartStore = useCartStore();
const { formatPrice } = useFormatPrice({ groupThousands: true });

const filterItems = (items?: BasketItem[]) => {
	return items?.filter((item) => !isShippingItem(item));
};

const hasDiscountCode = ref(false);
const discountCode = ref<string | null>(null);
const basketRef = toRef(() => props.basket);
const itemsRef = ref(filterItems(basketRef.value.Items));

const handleDiscountCode = (action: "apply" | "delete") => {
	cartStore.updateBasket({
		Id: basketRef.value.Id,
		OrderReference: props.orderId,
		DiscountCode: action === "apply" ? discountCode.value : null
	});

	if (action === "delete") {
		discountCode.value = null;
	}
};

const shippingItem = computed(() => {
	return basketRef.value.Items?.find((item) => isShippingItem(item));
});

const appliedPromotion = computed(
	() => basketRef.value?.AppliedPromotions || []
);

onMounted(() => {
	if (basketRef.value.DiscountCode) {
		discountCode.value = basketRef.value.DiscountCode;
		hasDiscountCode.value = true;
	}
});

watch(basketRef, () => {
	itemsRef.value = filterItems(basketRef.value.Items);
});
</script>
